import { MAX_WIDTH_CUSTOMIZED_TABLET } from '@components/Hooks/useWidth';
import { SIDEBAR, TOPBAR } from '@components/v2/layout/AppTemplate/appTemplate.consts';
import { SidebarState } from '@components/v2/layout/AppTemplate/appTemplate.types';
import { CSSObject, keyframes } from '@emotion/react';
import { Theme } from '@mui/material';
import { makeStyles } from 'tss-react/mui';

const TABLET_DOWN_MEDIA = `@media (max-width:${MAX_WIDTH_CUSTOMIZED_TABLET}px)`;
const SNAP_PAPER: CSSObject = { transition: 'none' };

interface StyleParams {
  sidebarState: SidebarState;
  isMobile: boolean;
  topSlotHeight: number;
  isPeekOpen?: boolean;
  isPeekFloating?: boolean;
  isPeekDocking?: boolean;
  isPeekUndocked?: boolean;
  isNested?: boolean;
}

type PeekStyles = Partial<Record<'drawerPaper' | 'inner' | 'navLayer' | 'sectionTitle', CSSObject>>;

type PeekPhase = Pick<
  StyleParams,
  'isPeekOpen' | 'isPeekFloating' | 'isPeekDocking' | 'isPeekUndocked' | 'isNested'
>;

const getDesktopWidth = (sidebarState: SidebarState) => {
  switch (sidebarState) {
    case 'expanded':
      return SIDEBAR.widthExpanded;
    case 'collapsed':
      return SIDEBAR.widthCollapsed;
    case 'hidden':
      return 0;
  }
};

const getNavLayerTransition = (theme: Theme, isMorphing?: boolean) =>
  [
    theme.transitions.create(['opacity', 'filter', 'transform', 'visibility'], {
      duration: 200,
      easing: 'ease',
    }),
    theme.transitions.create(isMorphing ? ['width', 'padding-top'] : 'width', {
      easing: theme.transitions.easing.sharp,
      duration: SIDEBAR.transitionDuration,
    }),
  ].join(', ');

const getMorphTransition = (theme: Theme, isFadingBackground?: boolean) =>
  theme.transitions.create(
    isFadingBackground
      ? ['left', 'border-radius', 'background-color', 'box-shadow']
      : ['left', 'border-radius', 'box-shadow'],
    {
      easing: theme.transitions.easing.sharp,
      duration: SIDEBAR.transitionDuration,
    },
  );

const getFloatingPeekStyles = (
  theme: Theme,
  { isPeekOpen, isPeekUndocked }: PeekPhase,
): PeekStyles => {
  const inset = theme.spacing(1);
  const offscreen = { transform: `translateX(calc(-100% - ${inset}))` };
  const slide = keyframes(isPeekOpen ? { from: offscreen } : { to: offscreen });

  return {
    ...(isPeekUndocked && { drawerPaper: SNAP_PAPER }),
    inner: {
      position: 'absolute',
      top: 0,
      left: inset,
      width: SIDEBAR.widthExpanded,
      height: `calc(100% - ${inset})`,
      overflow: 'hidden',
      borderRadius: theme.shape.borderRadiusLg,
      background: theme.palette.background.paper,
      boxShadow: theme.shadows[8],
      ...(isPeekUndocked && { transition: getMorphTransition(theme) }),
      animation:
        isPeekUndocked && isPeekOpen
          ? 'none'
          : `${slide} ${SIDEBAR.peekTransitionDuration}ms ${theme.transitions.easing.sharp} forwards`,
    },
    navLayer: {
      paddingTop: theme.spacing(1),
      ...(isPeekUndocked && { transition: getNavLayerTransition(theme, true) }),
    },
    sectionTitle: { opacity: 1, maxWidth: SIDEBAR.sectionTitleMaxWidth, transition: 'none' },
  };
};

const getDockingPeekStyles = (theme: Theme, isNested?: boolean): PeekStyles => ({
  ...(isNested && { drawerPaper: SNAP_PAPER }),
  inner: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: SIDEBAR.widthExpanded,
    height: '100%',
    background: theme.palette.background.default,
    transition: getMorphTransition(theme, true),
  },
  navLayer: { transition: getNavLayerTransition(theme, true) },
});

const getPeekStyles = (theme: Theme, phase: PeekPhase): PeekStyles | undefined => {
  if (phase.isPeekFloating) {
    return getFloatingPeekStyles(theme, phase);
  }

  if (phase.isPeekDocking) {
    return getDockingPeekStyles(theme, phase.isNested);
  }

  return undefined;
};

export const useSidebarStyles = makeStyles<StyleParams>()(
  (
    theme,
    {
      sidebarState,
      isMobile,
      topSlotHeight,
      isPeekOpen,
      isPeekFloating,
      isPeekDocking,
      isPeekUndocked,
      isNested,
    },
  ) => {
    const peek = getPeekStyles(theme, {
      isPeekOpen,
      isPeekFloating,
      isPeekDocking,
      isPeekUndocked,
      isNested,
    });
    const desktopWidth = getDesktopWidth(sidebarState);
    const mobileTopOffset = TOPBAR.height + topSlotHeight;
    const navLayerPaddingTop = theme.spacing(0.5);
    const navLayerPaddingTopTablet = theme.spacing(1);

    return {
      root: {
        ...(peek && { position: 'relative', zIndex: theme.zIndex.drawer }),
      },
      drawerPaper: {
        width: desktopWidth,
        overflow: 'hidden',
        position: 'relative',
        height: '100%',
        border: 'none',
        background: theme.palette.background.default,
        transition: theme.transitions.create('width', {
          easing: theme.transitions.easing.sharp,
          duration: SIDEBAR.transitionDuration,
        }),
        ...(peek && { overflow: 'visible' }),
        ...peek?.drawerPaper,
        ...(isMobile && {
          width: SIDEBAR.widthMobile,
          position: 'fixed',
          height: `calc(100dvh - ${mobileTopOffset}px - ${theme.spacing(2)})`,
          marginTop: mobileTopOffset,
          marginLeft: theme.spacing(1),
          borderRadius: theme.shape.borderRadiusLg,
          background: theme.palette.background.paper,
        }),
      },
      inner: {
        width: '100%',
        height: '100%',
        position: 'relative',
        top: 0,
        left: 0,
        display: 'flex',
        flexDirection: 'column',
        ...peek?.inner,
      },
      navRegion: {
        position: 'relative',
        flex: 1,
        minHeight: 0,
        width: '100%',
      },
      stickyTopSlot: {
        position: 'sticky',
        top: `-${navLayerPaddingTop}`,
        zIndex: 1,
        marginTop: `-${navLayerPaddingTop}`,
        paddingTop: navLayerPaddingTop,
        marginBottom: theme.spacing(2),
        backgroundColor: isMobile
          ? theme.palette.background.paper
          : theme.palette.background.default,
        [TABLET_DOWN_MEDIA]: {
          top: `-${navLayerPaddingTopTablet}`,
          marginTop: `-${navLayerPaddingTopTablet}`,
          paddingTop: navLayerPaddingTopTablet,
        },
      },
      bottomSlot: {
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1),
        width: '100%',
        boxSizing: 'border-box',
        padding: theme.spacing(1),
        '&:empty': {
          display: 'none',
        },
      },
      navLayer: {
        position: 'absolute',
        top: 0,
        left: 0,
        bottom: 0,
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        paddingTop: navLayerPaddingTop,
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(0.75),
        overflowY: 'auto',
        overflowX: 'hidden',
        boxSizing: 'border-box',
        scrollbarGutter: 'stable both-edges',
        transition: getNavLayerTransition(theme),
        ...peek?.navLayer,
        [TABLET_DOWN_MEDIA]: {
          paddingLeft: theme.spacing(0.75),
        },
      },
      navLayerVisible: {
        opacity: 1,
        filter: 'blur(0px)',
        transform: 'translateX(0)',
        pointerEvents: 'all',
        visibility: 'visible',
        [TABLET_DOWN_MEDIA]: {
          paddingTop: navLayerPaddingTopTablet,
        },
      },
      navLayerHidden: {
        opacity: 0,
        filter: 'blur(6px)',
        transform: 'translateX(16px)',
        pointerEvents: 'none',
        visibility: 'hidden',
      },
      sectionTitleSpaced: {
        marginTop: theme.spacing(2),
      },
      sectionTitle: {
        ...(theme.typography.overline as CSSObject),
        fontSize: '10px',
        paddingBottom: 0,
        opacity: sidebarState === 'expanded' ? 1 : 0,
        maxWidth: sidebarState === 'expanded' ? SIDEBAR.sectionTitleMaxWidth : 0,
        transition: theme.transitions.create(['opacity', 'max-width'], {
          duration: theme.transitions.duration.shorter,
          easing: theme.transitions.easing.sharp,
        }),
        ...peek?.sectionTitle,
        [TABLET_DOWN_MEDIA]: {
          opacity: 1,
          maxWidth: SIDEBAR.sectionTitleMaxWidth,
        },
      },
      backButton: {
        flexShrink: 0,
        minHeight: theme.spacing(2.5),
        paddingTop: theme.spacing(0.25),
        paddingBottom: theme.spacing(0.25),
        marginBottom: theme.spacing(1),
        borderRadius: theme.shape.borderRadius,
      },
    };
  },
);
